import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import documentCategoryService from '../../services/documentCategoryService';
import { toast } from 'react-hot-toast';

const DocumentCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', parentId: null });

  const load = async () => {
    try {
      setLoading(true);
      const res = await documentCategoryService.list();
      setCategories(res.data?.categories || res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await documentCategoryService.update(editing.id, form);
        toast.success('Category updated');
      } else {
        await documentCategoryService.create(form);
        toast.success('Category created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', parentId: null });
      load();
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    }
  };

  const remove = async (cat) => {
    if (!confirm('Delete this category?')) return;
    try {
      await documentCategoryService.remove(cat.id);
      toast.success('Category deleted');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Categories</h1>
            <p className="text-gray-600">Manage categories used in the Document Center</p>
          </div>
          <button onClick={() => { setShowForm(true); setEditing(null); }} className="px-3 py-2 bg-blue-600 text-white rounded-md flex items-center">
            <PlusIcon className="w-5 h-5 mr-2" /> New Category
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <ul className="divide-y divide-gray-200">
              {(categories || []).map((c) => (
                <li key={c.id} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{c.name}</div>
                    {c.parent && <div className="text-sm text-gray-500">Parent: {c.parent.name}</div>}
                  </div>
                  <div className="space-x-2">
                    <button className="text-indigo-600" onClick={() => { setEditing(c); setForm({ name: c.name, parentId: c.parentId || null }); setShowForm(true); }}>
                      <PencilIcon className="w-4 h-4 inline" />
                    </button>
                    <button className="text-red-600" onClick={() => remove(c)}>
                      <TrashIcon className="w-4 h-4 inline" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">{editing ? 'Edit Category' : 'New Category'}</h2>
              <form onSubmit={submit} className="space-y-3">
                <input className="border rounded px-3 py-2 w-full" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className="border rounded px-3 py-2 w-full" placeholder="Parent ID (optional)" value={form.parentId ?? ''} onChange={(e) => setForm({ ...form, parentId: e.target.value || null })} />
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentCategories;



